import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import { useProductTour } from '../../hooks/useProductTour';
import { useTheme } from '../../hooks/useTheme';
export function ProductTour() {
  const {
    isActive,
    currentStep,
    steps,
    skipTour,
    nextStep,
    prevStep,
    completeTour
  } = useProductTour();
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isLight = theme === 'light';
  const handleComplete = useCallback(() => {
    completeTour();
    navigate('/athena');
  }, [completeTour, navigate]);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const step = steps[currentStep];
  const updateTargetRect = useCallback(() => {
    if (!isActive || !step) return;
    const el = document.querySelector(step.target);
    if (el) {
      setTargetRect(el.getBoundingClientRect());
    } else {
      // If element not found, try again shortly (might be rendering)
      setTimeout(() => {
        const retryEl = document.querySelector(step.target);
        if (retryEl) setTargetRect(retryEl.getBoundingClientRect());
      }, 100);
    }
  }, [isActive, step]);
  useEffect(() => {
    updateTargetRect();
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
      updateTargetRect();
    };
    window.addEventListener('resize', handleResize);
    // Also update on scroll just in case
    window.addEventListener('scroll', updateTargetRect, true);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', updateTargetRect, true);
    };
  }, [updateTargetRect]);
  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') skipTour();
      if (e.key === 'ArrowRight' || e.key === 'Enter') nextStep();
      if (e.key === 'ArrowLeft') prevStep();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, nextStep, prevStep, skipTour]);
  if (!isActive || !step) return null;
  // Calculate tooltip position
  const getTooltipStyle = () => {
    if (!targetRect) return {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      opacity: 0
    };
    const gap = 16;
    const tooltipWidth = 320;
    const tooltipHeight = 220; // Approximate height (accounts for longer body text)
    let top = 0;
    let left = 0;
    let transform = '';
    switch (step.position) {
      case 'bottom':
        top = targetRect.bottom + gap;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        // Adjust if off-screen
        if (left + tooltipWidth > windowSize.width - 20) {
          left = windowSize.width - tooltipWidth - 20;
        }
        if (left < 20) left = 20;
        // If tooltip would go below viewport, flip to top
        if (top + tooltipHeight > windowSize.height - 20) {
          top = targetRect.top - gap - tooltipHeight;
        }
        break;
      case 'top':
        top = targetRect.top - gap - tooltipHeight;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        // If tooltip would go above viewport, flip to bottom
        if (top < 20) {
          top = targetRect.bottom + gap;
        }
        break;
      case 'right':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        left = targetRect.right + gap;
        // Adjust if off-screen vertically — anchor to bottom of viewport
        if (top + tooltipHeight > windowSize.height - 20) {
          top = windowSize.height - tooltipHeight - 20;
        }
        if (top < 20) top = 20;
        break;
      case 'left':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        left = targetRect.left - gap - tooltipWidth;
        // Adjust if off-screen vertically
        if (top + tooltipHeight > windowSize.height - 20) {
          top = windowSize.height - tooltipHeight - 20;
        }
        if (top < 20) top = 20;
        break;
    }
    return {
      top,
      left,
      opacity: 1
    };
  };
  // Calculate arrow position based on the adjusted tooltip position
  const getArrowStyle = () => {
    if (!targetRect) return {};
    const style = getTooltipStyle();
    const arrowSize = 8;
    const arrowColor = 'rgba(15, 23, 42, 0.95)';
    if (step.position === 'bottom') {
      const targetCenter = targetRect.left + targetRect.width / 2;
      let arrowLeft = targetCenter - style.left;
      arrowLeft = Math.max(20, Math.min(300, arrowLeft));
      return {
        top: -arrowSize,
        left: arrowLeft,
        borderLeft: `${arrowSize}px solid transparent`,
        borderRight: `${arrowSize}px solid transparent`,
        borderBottom: `${arrowSize}px solid ${arrowColor}`
      };
    }
    if (step.position === 'right') {
      const targetCenter = targetRect.top + targetRect.height / 2;
      let arrowTop = targetCenter - style.top;
      arrowTop = Math.max(20, Math.min(160, arrowTop));
      return {
        left: -arrowSize,
        top: arrowTop,
        borderTop: `${arrowSize}px solid transparent`,
        borderBottom: `${arrowSize}px solid transparent`,
        borderRight: `${arrowSize}px solid ${arrowColor}`
      };
    }
    return {};
  };
  const isLastStep = currentStep === steps.length - 1;
  const isUnnumbered = step?.unnumbered === true;
  const numberedSteps = steps.filter(s => !s.unnumbered);
  const numberedCount = numberedSteps.length;
  // For numbered steps, find the display index (1-based)
  const numberedIndex = isUnnumbered ? numberedCount : numberedSteps.findIndex(s => s.id === step?.id) + 1;
  return <div className="fixed inset-0 z-[10000] pointer-events-none" data-id="element-1045">
      {/* Spotlight Overlay */}
      {targetRect && <div className="absolute transition-all duration-500 ease-in-out pointer-events-auto" style={{
      top: targetRect.top - 4,
      left: targetRect.left - 4,
      width: targetRect.width + 8,
      height: targetRect.height + 8,
      borderRadius: '16px',
      boxShadow: isLight ? '0 0 0 9999px rgba(0, 0, 0, 0.35)' : '0 0 0 9999px rgba(0, 0, 0, 0.65)',
      zIndex: 10000
    }} data-id="element-1046" />}

      {/* Tooltip */}
      <AnimatePresence mode="wait" data-id="element-1047">
        <motion.div key={currentStep} initial={{
        opacity: 0,
        y: 10,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -10,
        scale: 0.95
      }} transition={{
        type: 'spring',
        stiffness: 400,
        damping: 30
      }} className={`absolute w-[320px] rounded-xl pointer-events-auto border border-white/[0.1] shadow-[0_0_40px_rgba(236,72,153,0.15)]`} style={{
        ...getTooltipStyle(),
        background: 'rgba(15, 23, 42, 0.95)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        zIndex: 10001
      }} data-id="element-1048">
          {/* Arrow */}
          <div className="absolute w-0 h-0" style={getArrowStyle()} data-id="element-1049" />

          <div className="p-5" data-id="element-1050">
            {/* Header */}
            <div className="flex items-start justify-between mb-3" data-id="element-1051">
              <div className="flex items-center gap-2" data-id="element-1052">
                {isUnnumbered ? <div className="w-6 h-6 rounded-full bg-[#EC4899]/20 flex items-center justify-center text-[#EC4899]" data-id="element-1053">
                    <Home size={12} data-id="element-1054" />
                  </div> : <>
                    <div className="w-6 h-6 rounded-full bg-[#EC4899]/20 flex items-center justify-center text-[#EC4899] text-xs font-bold" data-id="element-1055">
                      {numberedIndex}
                    </div>
                    <span className="text-xs font-medium uppercase tracking-wider text-slate-400" data-id="element-1056">
                      Step {numberedIndex} of {numberedCount}
                    </span>
                  </>}
              </div>
              <button onClick={skipTour} className="transition-colors p-1 rounded-md text-slate-500 hover:text-white hover:bg-white/10" data-id="element-1057">
                <X size={14} data-id="element-1058" />
              </button>
            </div>

            {/* Content */}
            <h3 className="text-lg font-bold mb-2 text-white" data-id="element-1059">{step.title}</h3>
            <p className="text-sm leading-relaxed mb-6 text-slate-300" data-id="element-1060">
              {step.body}
            </p>

            {/* Footer */}
            <div className="flex items-center justify-between mt-auto" data-id="element-1061">
              {!isUnnumbered ? <div className="flex gap-1" data-id="element-1062">
                  {numberedSteps.map((_, idx) => <div key={idx} className={`w-1.5 h-1.5 rounded-full transition-colors ${idx === numberedIndex - 1 ? 'bg-[#EC4899]' : 'bg-slate-700'}`} data-id="element-1063" />)}
                </div> : <div className="flex gap-1" data-id="element-1064">
                  {numberedSteps.map((_, idx) => <div key={idx} className="w-1.5 h-1.5 rounded-full bg-[#EC4899]" data-id="element-1065" />)}
                </div>}

              <div className="flex items-center gap-2" data-id="element-1066">
                {currentStep > 0 && <Button variant="ghost" size="sm" onClick={prevStep} className="h-8 px-2 text-slate-400 hover:text-white" data-id="element-1067">
                    Back
                  </Button>}
                <Button size="sm" onClick={isLastStep ? handleComplete : nextStep} className="bg-[#EC4899] hover:bg-pink-600 text-white h-8 px-4" data-id="element-1068">
                  {isLastStep ? "Let's go!" : 'Next'}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>;
}